import type { ComponentWidgetConfig, WidgetType } from 'components/shared/base-product-tile/base-product-tile-types.js'
import { retailPriceVariants } from 'components/discovery/product/retail-price/retail-price.js'
import { vatModes } from 'components/discovery/product/price/price.js'
import { featuredAttributeDisplayModes, fontVariants as featuredAttributeFontVariants } from 'components/discovery/product/featured-attributes/featured-attributes.js'
import { brandLayouts, fontVariants as brandFontVariants } from 'components/discovery/product/brand/brand.js'
import { fontVariants as titleFontVariants } from 'components/discovery/product/tile-parts/parts/title/tile-title.js'
import { fontVariants as leviesFontVariants } from 'components/discovery/product/levies/levies.js'
import { iconStyles } from '@cloudsuite/mosaic-shared/icons/library.js'
import { triggerVariant as orderTemplateTriggerVariants } from 'components/discovery/product/add-to-order-templates/components/order-template-slide-in/order-template-slide-in.js'


export type PropertyFieldType = 'boolean' | 'string' | 'number' | 'enum' | 'hidden-fields'

export interface PropertyOption {
  value: string
  label: string
}

export interface PropertyFieldSchema {
  key: string
  label: string
  type: PropertyFieldType
  options?: readonly PropertyOption[]
  min?: number
  max?: number
  step?: number
  helpText?: string
  /** Only show helpText when this field's own current value equals this — unlike visibleIf, which gates the whole field on ANOTHER field's value. */
  helpTextVisibleFor?: boolean | string
  /** `default` is what the component itself uses while that other property is unset, so an imported widget without it still shows the right fields. */
  visibleIf?: { key: string; equals: boolean | string; default?: boolean | string }
  group?: string
}

export const isFieldVisible = (schema: PropertyFieldSchema, properties: ComponentWidgetConfig): boolean => {
  const visibleIf = schema.visibleIf
  if (!visibleIf) return true
  const actual = (properties as Record<string, unknown>)[visibleIf.key] ?? visibleIf.default
  return actual === visibleIf.equals
}

const enumOptions = (values: readonly string[]): PropertyOption[] =>
  values.map(value => ({ value, label: value }))

const priceSources = ['sale', 'consumer', 'customer', 'list', 'sale-taxed', 'sale-display', 'per-price-uom'] as const

const priceFieldsForSlot = (slot: 1 | 2): PropertyFieldSchema[] => {
  const group = `Price ${slot}`
  const sourceOptions = slot === 1 ? enumOptions(priceSources) : [{ value: '', label: 'None' }, ...enumOptions(priceSources)]
  return [
    { key: `price${slot}Source`, label: 'Source', type: 'enum', group, options: sourceOptions, ...(slot === 2 && { helpText: 'The second price is only shown when a source is selected.' }) },
    { key: `price${slot}Variant`, label: 'Variant', type: 'enum', group, options: enumOptions(['default', 'sale']) },
    { key: `price${slot}Size`, label: 'Size', type: 'enum', group, options: enumOptions(['l', 'm', 's']) },
    { key: `price${slot}Layout`, label: 'Layout', type: 'enum', group, options: enumOptions(['inline', 'inline-vat-below', 'lead-inline', 'lead-inline-vat-below', 'inline-meta-below', 'stacked']) },
    { key: `price${slot}Lead`, label: 'Lead text', type: 'string', group },
    { key: `price${slot}Uom`, label: 'Unit of measure', type: 'string', group },
    { key: `price${slot}Vat`, label: 'VAT text', type: 'string', group },
    { key: `price${slot}ShowVat`, label: 'Show VAT', type: 'boolean', group },
    { key: `price${slot}VatMode`, label: 'VAT mode', type: 'enum', group, options: [{ value: '', label: 'Automatic' }, ...enumOptions(vatModes)] },
  ]
}

export const WIDGET_PROPERTY_SCHEMAS: Record<WidgetType, PropertyFieldSchema[]> = {
  'csc-product-tile-title': [
    { key: 'fontVariant', label: 'Font variant', type: 'enum', options: enumOptions(titleFontVariants) },
  ],
  'csc-product-tile-product-code': [
    { key: 'hiddenFields', label: 'Hidden parts', type: 'hidden-fields', options: [{ value: 'label', label: 'Hide "code:" label' }] },
  ],
  'csc-price': [
    { key: 'useCscPrice', label: 'Use CSC price formatting', type: 'boolean', group: 'General' },
    { key: 'hiddenFields', label: 'Hidden parts', type: 'hidden-fields', group: 'General', options: [{ value: 'price1', label: 'Hide price 1' }, { value: 'price2', label: 'Hide price 2' }, { value: 'uom', label: 'Hide UOM' }] },
    ...priceFieldsForSlot(1),
    ...priceFieldsForSlot(2),
  ],
  'csc-retail-price': [
    { key: 'fontVariant', label: 'Font variant', type: 'enum', options: enumOptions(retailPriceVariants) },
  ],
  'csc-product-tile-variant-indication': [
    { key: 'badgeVariant', label: 'Badge variant', type: 'enum', options: enumOptions(['default', 'error', 'info', 'success', 'warning', 'new', 'our-choice', 'bestseller', 'sale', 'premium']) },
  ],
  'csc-product-tile-variants': [
    { key: 'displayLimit', label: 'Max variants shown', type: 'number', min: 1, step: 1 },
  ],
  'csc-featured-attributes': [
    { key: 'displayMode', label: 'Display mode', type: 'enum', options: [{ value: '', label: 'All display modes' }, ...enumOptions(featuredAttributeDisplayModes)], helpText: 'Only show attributes with this display mode. Add separate widgets per mode to split them across the tile.' },
    { key: 'inline', label: 'Inline layout', type: 'boolean' },
    { key: 'listVariant', label: 'List variant', type: 'enum', options: enumOptions(['unstyled', 'bullet', 'custom-icon']) },
    { key: 'iconName', label: 'List icon name', type: 'string', visibleIf: { key: 'listVariant', equals: 'custom-icon' } },
    { key: 'iconStyle', label: 'List icon style', type: 'string', visibleIf: { key: 'listVariant', equals: 'custom-icon' } },
    { key: 'divider', label: 'Show divider', type: 'boolean' },
    { key: 'fontVariant', label: 'Font variant', type: 'enum', options: enumOptions(featuredAttributeFontVariants) },
  ],
  'csc-stock-indication': [
    { key: 'variant', label: 'Variant', type: 'enum', options: enumOptions(['default', 'simple', 'minimal', 'solid', 'floating']) },
  ],
  'csc-product-tile-tags': [
    { key: 'inline', label: 'Inline layout', type: 'boolean', helpText: 'Only relevant if the product has multiple tags.' },
  ],
  'csc-plp-cta-button': [
    { key: 'type', label: 'Type', type: 'enum', options: enumOptions(['view-product', 'configurator-product', 'login-to-order', 'choose-variant']) },
    { key: 'size', label: 'Size', type: 'enum', options: enumOptions(['default', 'small']) },
    { key: 'fit', label: 'Fit', type: 'enum', options: enumOptions(['auto', 'fill', 'square']) },
  ],
  'csc-order-form': [
    { key: 'layout', label: 'Layout', type: 'enum', options: enumOptions(['default', 'vertical']) },
    { key: 'showButtonLabel', label: 'Show button label', type: 'boolean' },
    { key: 'hiddenFields', label: 'Hidden parts', type: 'hidden-fields', options: [{ value: 'stepper', label: 'Hide quantity stepper' }, { value: 'uom', label: 'Hide unit of measure' }] },
    { key: 'fit', label: 'Fit', type: 'enum', options: enumOptions(['auto', 'fill', 'square']) },
    { key: 'uomSource', label: 'Unit of measure', type: 'enum', options: [{ value: 'default_uom', label: 'Default unit (default_uom)' }, { value: 'sale_uom', label: 'Sale unit (sale_uom)' }], helpText: 'Which product field to show next to the stepper, unless the unit of measure is hidden.' },
  ],
  'csc-compare-checkbox': [
    { key: 'variant', label: 'Variant', type: 'enum', options: enumOptions(['default', 'with-background']) },
    { key: 'priceSource', label: 'Price source', type: 'enum', options: enumOptions(['sale', 'consumer', 'customer', 'list', 'sale-taxed', 'sale-display', 'per-price-uom']), helpText: 'Which price to show for this product in the compare bar; use the same source as the tile\'s price.' },
  ],
  'csc-quotation-add-product': [
    { key: 'variant', label: 'Variant', type: 'enum', options: enumOptions(['button', 'icon-button', 'link']) },
    { key: 'buttonVariant', label: 'Button variant', type: 'enum', options: enumOptions(['default', 'primary', 'secondary', 'error', 'info', 'success', 'warning']), visibleIf: { key: 'variant', equals: 'button', default: 'button' } },
    { key: 'iconVariant', label: 'Icon variant', type: 'enum', options: enumOptions(['primary', 'secondary', 'ghost']), visibleIf: { key: 'variant', equals: 'icon-button', default: 'button' } },
    { key: 'linkVariant', label: 'Link variant', type: 'enum', options: enumOptions(['primary', 'secondary', 'white']), visibleIf: { key: 'variant', equals: 'link', default: 'button' } },
    { key: 'fit', label: 'Fit', type: 'enum', options: enumOptions(['auto', 'fill', 'square']), visibleIf: { key: 'variant', equals: 'button', default: 'button' } },
  ],
  'csc-product-tile-add-to-order-template': [
    { key: 'iconName', label: 'Icon name', type: 'string' },
    { key: 'variant', label: 'Variant', type: 'string', helpText: 'A cse-icon-button/cse-link variant, e.g. "primary" or "secondary".' },
    { key: 'asLink', label: 'Render as link', type: 'boolean' },
    { key: 'useLoginSlideIn', label: 'Use login slide-in', type: 'boolean', helpText: 'When the user is not logged in, open csc-login-slide-in instead of the theme’s #login-sidebar.' },
  ],
  'csc-brand': [
    { key: 'layout', label: 'Layout', type: 'enum', options: enumOptions(brandLayouts), helpText: 'If the product has no brand image, the text version is shown instead.', helpTextVisibleFor: 'image' },
    { key: 'fontVariant', label: 'Font variant', type: 'enum', options: enumOptions(brandFontVariants) },
  ],
  'csc-levies': [
    { key: 'fontVariant', label: 'Font variant', type: 'enum', options: enumOptions(leviesFontVariants) },
    { key: 'listVariant', label: 'List item variant', type: 'enum', options: enumOptions(['unstyled', 'bullet', 'custom-icon']) },
    { key: 'listIconName', label: 'List item icon name', type: 'string', visibleIf: { key: 'listVariant', equals: 'custom-icon' } },
    { key: 'listIconStyle', label: 'List item icon style', type: 'enum', options: enumOptions(iconStyles), visibleIf: { key: 'listVariant', equals: 'custom-icon' } },
  ],
  'csc-product-rating': [],
  'csc-order-template-slide-in': [
    { key: 'triggerType', label: 'Trigger type', type: 'enum', options: enumOptions(['icon-button', 'link']) },
    { key: 'triggerVariant', label: 'Trigger variant', type: 'enum', options: enumOptions(['primary', 'secondary']), visibleIf: { key: 'triggerType', equals: 'icon-button', default: 'link' } },
    { key: 'triggerVariant', label: 'Trigger variant', type: 'enum', options: enumOptions(orderTemplateTriggerVariants), visibleIf: { key: 'triggerType', equals: 'link', default: 'link' } },
    { key: 'triggerSize', label: 'Trigger size', type: 'enum', options: enumOptions(['default', 'small']) },
    { key: 'triggerIconName', label: 'Trigger icon name', type: 'string' },
  ],
}
